import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient, isGlobalGatingHandledError } from '@/services/api';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { toast } from '@/utils/toast';
import { teamQueryKeys } from '@/lib/queryKeys';

/**
 * Trend Suggestion Item
 */
export interface TrendSuggestionItem {
  id: number;
  topic: string;
  description: string | null;
  trendScore: string;
  category: string | null;
  actionTip: string | null;
  hashtags: string[];
  generatedAt: string;
}

/**
 * Trend Suggestions Response
 */
export interface TrendSuggestionsData {
  items: TrendSuggestionItem[];
  needsRefresh: boolean;
  hasIndustries: boolean;
  lastGenerated: string | null;
}

interface TrendSuggestionsApiResponse {
  success: boolean;
  data: TrendSuggestionsData;
}

interface RefreshTrendsApiResponse {
  success: boolean;
  data: { items: TrendSuggestionItem[] };
}

/**
 * Fetch trend suggestions from API
 */
const fetchTrendSuggestions = async (): Promise<TrendSuggestionsData> => {
  const { data } = await apiClient.get<TrendSuggestionsApiResponse>('/api/dashboard/trends');
  return data.data;
};

/**
 * Hook to fetch trend suggestions
 */
export const useTrendSuggestions = () => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: ['dashboard-trends', teamId],
    queryFn: fetchTrendSuggestions,
    enabled: hasTeam,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to refresh trend suggestions
 */
export const useRefreshTrends = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<RefreshTrendsApiResponse>('/api/dashboard/trends/refresh', undefined, {
        timeout: 90000, // AI generation with web search can take 30-60s
      });
      return data.data.items;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-trends', teamId] });
      // Applies the team's DEFAULT brand profile server-side — invalidate so
      // its usage count/last-used updates live without a manual refresh.
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      toast.success('Trends updated!');
    },
    onError: (error: AxiosError<{ error: string }>) => {
      if (isGlobalGatingHandledError(error)) return;
      const message = error.response?.data?.error || 'Failed to refresh trends';
      toast.error(message);
    },
  });
};
